import {
  FieldWrapper,
  type FieldWrapperInjectedProps,
  getFieldErrorMessage,
} from "@birdwatch/ui/form";
import { type ReactNode, useState } from "react";
import { Input } from "@/components/ui/input/input";
import { PriceInput } from "@/components/ui/input/price-input";
import {
  type FieldBindingProps,
  PolyField,
  pickBinding,
} from "@/fields/_internal/poly-field";
import { moneyInputTextOf, parseMoneyText } from "./money-text";

type MoneyFieldBaseProps = {
  label?: ReactNode;
  optional?: boolean;
  disabled?: boolean;
  allowNegative?: boolean;
  placeholder?: string;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the inner input. */
  inputClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  prefixClassName?: string;
  suffixClassName?: string;
  clearable?: boolean;
  autoFocus?: boolean;
  onBlur?: () => void;
} & (
  | {
      /**
       * When true (default), the form value is coerced to `number` on change and
       * stringified for display. When false, the raw string is stored and emitted.
       */
      coerceToNumber?: true;
      /**
       * Show the amount with thousands separators ("1,000,000"); the form still
       * stores the plain number. While the person types, their own text stays.
       */
      groupThousands?: boolean;
    }
  | { coerceToNumber: false; groupThousands?: never }
);

export type MoneyFieldProps = MoneyFieldBaseProps & FieldBindingProps;

type GroupedMoneyInputProps = FieldWrapperInjectedProps & {
  value: number | null;
  onChange: (amount: number | null) => void;
  onBlur: () => void;
  allowNegative: boolean;
  placeholder?: string;
  className?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  prefixClassName?: string;
  suffixClassName?: string;
  clearable?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
};

/**
 * The amount with its thousands separators. Declared at module scope so it
 * keeps its typed text (and the caret) across the form's re-renders.
 */
function GroupedMoneyInput({
  value,
  onChange,
  onBlur,
  allowNegative,
  ...inputProps
}: GroupedMoneyInputProps) {
  // The text typed since the input took focus; null when not typing.
  const [typed, setTyped] = useState<string | null>(null);
  return (
    <Input
      {...inputProps}
      inputMode="decimal"
      maxLength={100}
      value={moneyInputTextOf(typed, value, allowNegative)}
      onChange={(event) => {
        const reading = parseMoneyText(event.target.value, allowNegative);
        if (!reading.valid) return;
        setTyped(event.target.value);
        onChange(reading.amount);
      }}
      onBlur={() => {
        setTyped(null);
        onBlur();
      }}
    />
  );
}

export function MoneyField(props: MoneyFieldProps) {
  const {
    label,
    optional,
    disabled,
    allowNegative,
    placeholder,
    className,
    inputClassName,
    prefix,
    suffix,
    prefixClassName,
    suffixClassName,
    clearable,
    autoFocus,
    onBlur,
    coerceToNumber = true,
    groupThousands = false,
  } = props;
  const binding = pickBinding(props);
  return (
    <PolyField<number | string | null> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          optional={optional}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={disabled}
        >
          {groupThousands ? (
            <GroupedMoneyInput
              value={
                typeof field.state.value === "number" ? field.state.value : null
              }
              onChange={(amount) => field.handleChange(amount)}
              onBlur={() => {
                field.handleBlur();
                onBlur?.();
              }}
              allowNegative={allowNegative ?? false}
              placeholder={placeholder}
              className={inputClassName}
              prefix={prefix}
              suffix={suffix}
              prefixClassName={prefixClassName}
              suffixClassName={suffixClassName}
              clearable={clearable}
              autoFocus={autoFocus}
              disabled={disabled}
            />
          ) : (
            <PriceInput
              value={field.state.value == null ? "" : String(field.state.value)}
              onChange={(value) => {
                // PriceInput's own `onChange` intersects the DOM input handler it
                // spreads onto, so the parameter widens to include a ChangeEvent.
                // At runtime it only ever emits the validated raw string.
                const raw =
                  typeof value === "string" || typeof value === "number"
                    ? String(value)
                    : "";
                const next = coerceToNumber
                  ? raw === ""
                    ? null
                    : Number(raw)
                  : raw;
                field.handleChange(next);
              }}
              onBlur={() => {
                field.handleBlur();
                onBlur?.();
              }}
              allowNegative={allowNegative}
              placeholder={placeholder}
              className={inputClassName}
              prefix={prefix}
              suffix={suffix}
              prefixClassName={prefixClassName}
              suffixClassName={suffixClassName}
              clearable={clearable}
              autoFocus={autoFocus}
              disabled={disabled}
            />
          )}
        </FieldWrapper>
      )}
    </PolyField>
  );
}
